import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Tooltip from "@material-ui/core/Tooltip";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import FeatureGate from "../FeatureGate";
import * as actionCreators from "../../actions/actionCreators";
import { bindActionCreators } from "redux";

// Constants reused (avoid reallocation & reduce branches inside map)
const UNCLICKABLE = new Set(["divider", "description"]);
// Whitelisted stateless section components we safely expand to expose their link children.
// (These components must stay hook-free; if hooks are added later, remove from this list.)
// NOTE: Relying only on function names breaks under production minification. We therefore support
// an explicit static boolean marker __localNavSection placed on the function object. During the
// transition we keep the legacy name set for backward compatibility.
const SECTION_COMPONENTS = new Set([
    "AdminRoleInfo",
    "AdminGlobalLinks",
    "CompanyFeatureAccessSection",
    "CompanyFieldManagementSection",
    "SuperAdminExtrasSection",
    "BrandingAndSystemSection",
]);
const baseMenuItemStyle = Object.freeze({
    fontSize: 13,
    color: "#575755 !important",
    textDecoration: "none",
});

// Expand child elements (fragments & lightweight function components w/out hooks) into flat list
function expandChild(child, recurse) {
    if (Array.isArray(child)) return child.flatMap(c => recurse(c, recurse));
    if (child == null || child === false) return [];
    if (!React.isValidElement(child)) return [child];

    const typeName = child.type && child.type.name;

    // Atomic leaves: native elements, link components, divider, description
    if (
        typeof child.type === "string" ||
        ["LocalNavSoftLink", "LocalNavHardLink", "Divider"].includes(typeName) ||
        (child.props && (child.props.name === "divider" || child.props.name === "description"))
    ) {
        return [child];
    }

    // Fragments: flatten children
    if (child.type === React.Fragment) {
        return React.Children.toArray(child.props.children).flatMap(c => recurse(c, recurse));
    }

    // Whitelisted section components: execute to extract their internal links (must remain hook-free)
    // Accept either legacy name-based whitelist OR explicit static marker to survive minification.
    if ((SECTION_COMPONENTS.has(typeName) || child.type.__localNavSection) && typeof child.type === "function") {
        try {
            const rendered = child.type(child.props);
            return recurse(rendered, recurse);
        } catch {
            return [child]; // Fallback if unexpected hooks are added
        }
    }

    // All other components left intact
    return [child];
}

function LocalNavMenu({ text, children, feature, featureArrayMode, isCollapsed, collapsedIcon }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpenMenu = useCallback(e => setAnchorEl(e.currentTarget), []);
    const handleClose = useCallback(() => setAnchorEl(null), []);

    const expanded = useMemo(
        () => React.Children.toArray(children).flatMap(c => expandChild(c, expandChild)),
        [children]
    );

    const menuItems = useMemo(
        () =>
            expanded.map((child, key) => {
                if (!child) return null;
                if (child.props && UNCLICKABLE.has(child.props.name)) return child; // pass through divider/description

                const needsGate = child.props && child.props.feature;
                const content = (
                    <MenuItem disableGutters key={key} style={baseMenuItemStyle} onClick={handleClose}>
                        {child}
                    </MenuItem>
                );
                if (needsGate) {
                    return (
                        <FeatureGate
                            key={key}
                            feature={child.props.feature}
                            featureArrayMode={child.props.featureArrayMode || "OR"}
                        >
                            {content}
                        </FeatureGate>
                    );
                }
                return content;
            }),
        [expanded, handleClose]
    );

    return (
        <FeatureGate feature={feature} featureArrayMode={featureArrayMode}>
            {!isCollapsed ? (
                <span aria-controls={`${text}-menu`} onClick={handleOpenMenu}>
                    {text}
                </span>
            ) : (
                <span aria-controls={`${text}-menu`}>
                    <Tooltip title={text} style={{ fontSize: 12 }} placement="right">
                        <img onClick={handleOpenMenu} className={"collapsed-icon"} src={collapsedIcon} />
                    </Tooltip>
                </span>
            )}
            <Menu
                id={`${text}-menu`}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                className="left-nav-menu"
            >
                {menuItems}
            </Menu>
        </FeatureGate>
    );
}

LocalNavMenu.propTypes = {
    children: PropTypes.any,
    text: PropTypes.string.isRequired,
    feature: PropTypes.any,
    featureArrayMode: PropTypes.string,
    enabledFeatures: PropTypes.array,
    isCollapsed: PropTypes.bool,
    collapsedIcon: PropTypes.string,
};

const mapStateToProps = state => ({
    enabledFeatures: state.session.enabledFeatures || [],
});

const mapDispatchToProps = dispatch => bindActionCreators(actionCreators, dispatch);

const LocalNavMenuC = connect(mapStateToProps, mapDispatchToProps)(LocalNavMenu);

export default LocalNavMenuC;
